import { Anime, LineNotifyConfig } from '../types/anime';
import { INITIAL_ANIME_LIST } from '../data/initialData';

const ANIME_STORAGE_KEY = 'anime_ranker_list_v1';
const LINE_CONFIG_KEY = 'anime_ranker_line_config_v1';

export const DEFAULT_LINE_CONFIG: LineNotifyConfig = {
  enabled: true,
  mode: 'line_messaging',
  channelAccessToken: '',
  targetId: '',
  webhookUrl: '',
  notifyOnSchedule: true,
  notifyOnNewEpFound: true,
  autoCheckMinutes: 30,
};

import { clearGenericThaiTitles, dropCompilationSources, mergeDuplicateAnime } from '../utils/helpers';

/**
 * Data fix: "Reborn as a Goblin" was wrongly linked to Goblin Slayer on WeTV, which brought in
 * 12 Goblin Slayer episodes and inflated every source to ep 12. Strip WeTV + those episodes.
 * Runs once per browser (flag), and on cloud lists pulled in that same session.
 */
const GOBLIN_VERIFIED_LATEST_EP = 4; // Ani-One playlist / Bilibili / Waku all at ep 4 (2026-10-20)

/**
 * Data fix: a new season got merged into the old season's entry (title without a season number
 * matched "Season N"), so the card showed the old season's year and episode count.
 * Values verified against Ani-One / Bilibili / Anime-Waku on 2026-10-20.
 */
const SEASON_SPLIT_FIXES: Array<{
  id: string;
  match: RegExp;
  patch: Partial<Anime>;
  wakuUrl: string;
}> = [
  {
    id: 'ANIME_1792438362518_r619',
    match: /ao\s*ashi|aoashi|แข้งเด็กหัวใจนักสู้/i,
    wakuUrl: 'https://anime-waku.com/anime/ao-ashi-season-2/',
    patch: {
      title: 'Ao Ashi Season 2',
      thaiTitle: 'แข้งเด็กหัวใจนักสู้ (ภาค2)',
      season: 'FALL',
      year: 2026,
      latestEp: 3,
      totalEp: 0,
      status: 'กำลังฉาย',
      broadcastDay: 'MON',
      broadcastTime: '11:00',
    },
  },
  {
    id: 'ANIME_1792438315222_1b8d',
    match: /kantei\s*skill|สกิลประเมิน/i,
    wakuUrl: 'https://anime-waku.com/anime/tensei-kizoku-kantei-skill-de-nariagaru-season-3/',
    patch: {
      title: 'Tensei Kizoku Kantei Skill de Nariagaru Season 3',
      thaiTitle: 'เกิดใหม่เป็นขุนนาง ขึ้นเป็นใหญ่ด้วยสกิลประเมิน ซีซัน 3',
      season: 'FALL',
      year: 2026,
      latestEp: 4,
      totalEp: 0,
      status: 'กำลังฉาย',
      broadcastDay: 'SUN',
      broadcastTime: '22:00',
      imageUrl: 'https://pic.bstarstatic.com/ogv/98d9fce13c9e17896d5147f94ab1a470.png',
    },
  },
];

/**
 * Data fix: adding the dub version of Ranma ½ (2024) was dropped because the entry already had
 * a Waku (sub) link. Add the dub page back so both audio tracks and their episodes show.
 */
function applySubDubFix(a: Anime): Anime | null {
  if (a.id !== 'ANIME_1792438913561_ki3a' || !/ranma/i.test(a.title)) return null;
  const dubUrl = 'https://anime-waku.com/anime/ranma-%c2%bd-th-2024/';
  if (a.sources.some((s) => s.url === dubUrl)) return null;
  return {
    ...a,
    totalEp: 24, // season 1 + 2 (12 + 12); the saved 14 was wrong
    audioType: 'ซับ+พากย์',
    sources: [
      ...a.sources,
      { type: 'anime-waku', name: 'Anime-Waku (พากย์ไทย)', url: dubUrl, latestEp: 24, audio: 'พากย์ไทย' },
    ],
    episodes: [], // detail popup re-fetches sub + dub lists
  };
}

function applyGoblinFix(a: Anime): Anime | null {
  if (!/reborn as a goblin|tensei goblin|เกิดใหม่เป็นก็อบลิน/i.test(`${a.title} ${a.thaiTitle || ''}`)) {
    return null;
  }
  // Inflated numbers (12 from Goblin Slayer, 6 from a mislabeled Waku link) only ever go up
  // through auto-checks, so reset them to the verified count; later episodes are picked up normally.
  const latestEp = GOBLIN_VERIFIED_LATEST_EP;
  return {
    ...a,
    latestEp,
    status: 'กำลังฉาย',
    sources: (a.sources || [])
      .filter((s) => s.type !== 'wetv' && !(s.url || '').includes('wetv.vip'))
      .map((s) => ({ ...s, latestEp })),
    episodes: [], // detail popup re-fetches the live list from the remaining sources
  };
}

function applySeasonSplitFix(a: Anime): Anime | null {
  const fix = SEASON_SPLIT_FIXES.find((f) => f.id === a.id && f.match.test(`${a.title} ${a.thaiTitle || ''}`));
  if (!fix) return null;
  const latestEp = fix.patch.latestEp ?? a.latestEp;
  return {
    ...a,
    ...fix.patch,
    // Point the Waku link at the new season's page; other sources already are the new season
    sources: (a.sources || []).map((s) =>
      s.type === 'anime-waku' ? { ...s, url: fix.wakuUrl, latestEp } : { ...s, latestEp }
    ),
    episodes: [],
  };
}

/**
 * Data fix: adding Bleach TYBW Part 1 merged in Calamity's (Part 4) Bilibili link and Ani-One
 * dub playlist, because every "Bleach" title matched — so the card showed Calamity's ep 49.
 * Keep it as Part 1 only, with Bilibili's real Part 1 page.
 */
function applyBleachPartFix(a: Anime): Anime | null {
  if (a.id !== 'ANIME_1792440267043_3tmp' || !/bleach|บลีช/i.test(`${a.title} ${a.thaiTitle || ''}`)) {
    return null;
  }
  const calamityLinks = ['bilibili.tv/th/play/2414568', 'list=PLZYdppx7fd7c'];
  const sources = (a.sources || []).filter((s) => !calamityLinks.some((l) => s.url.includes(l)));
  if (!sources.some((s) => s.type === 'bilibili')) {
    sources.push({
      type: 'bilibili',
      name: 'Bilibili',
      url: 'https://www.bilibili.tv/th/play/2069614',
      latestEp: 13,
      audio: 'ซับไทย',
    });
  }
  return {
    ...a,
    season: 'FALL',
    year: 2022,
    latestEp: 13,
    totalEp: 13,
    status: 'จบแล้ว',
    broadcastDay: 'UNKNOWN', // Mon 22:30 was Calamity's slot
    broadcastTime: '',
    imageUrl: 'https://anime-waku.com/wp-content/uploads/2004/10/2EewmxXe72ogD0EaWM8gqa0ccIw.jpg',
    sources: sources.map((s) => ({ ...s, latestEp: 13 })),
    episodes: [],
  };
}

/**
 * Data fix for the separately re-added Bleach TYBW parts (2026-10-21):
 * - Part 1 got Ani-One's all-parts playlist (49 eps) before the compilation check shipped,
 *   which also set its total to 49 — so the always-on check can't tell. Back to 13/13.
 * - Calamity was saved from a week whose episode was posted late (Mon 22:30); its official
 *   Bilibili slot is Sat 22:00.
 */
function applyBleachSplitFix(a: Anime): Anime | null {
  if (a.id === 'ANIME_1792441377101_6t7u' && /บลีช|bleach/i.test(a.title)) {
    const sources = a.sources.filter((s) => !s.url.includes('PL_YV8EWtbQXHorKU59EjVs-wWv47Jhq8d'));
    return { ...a, sources, latestEp: 13, totalEp: 13, status: 'จบแล้ว', episodes: [] };
  }
  if (a.id === 'ANIME_1792441383077_ugkp' && /บทภัยพิบัติ|kashin/i.test(a.title)) {
    return {
      ...a,
      broadcastDay: 'SAT',
      broadcastTime: '22:00',
      detectedDayNote: 'ตอนใหม่ทุกวันเสาร์ เวลา 22:00 น. (Bilibili)',
    };
  }
  return null;
}

/** Overgeared premiered Fall 2026 (Prime Video Sep 27, TV Oct 2); Waku's September upload read as Summer */
function applyOvergearedSeasonFix(a: Anime): Anime | null {
  if (a.id === 'ANIME_1792437197448_jh47' && a.season === 'SUMMER' && a.year === 2026) {
    return { ...a, season: 'FALL' };
  }
  return null;
}

/**
 * Added from Bilibili's airing timeline, which labelled everything as the current season (Fall).
 * Bilibili's premiere dates: Exiled Heavy Knight 2 Jul 2026 (Summer), Re:ZERO S4 3 Jun 2026 (Spring).
 */
function applyBilibiliSeasonFix(a: Anime): Anime | null {
  if (a.season !== 'FALL' || a.year !== 2026) return null;
  if (a.id === 'ANIME_1792641374853_lfav') return { ...a, season: 'SUMMER' };
  if (a.id === 'ANIME_1792310635360_nnrz') return { ...a, season: 'SPRING' };
  return null;
}

/**
 * Moved from EX to A before tier changes went to the end of the new tier, so they kept EX's
 * position 2 and jumped ahead of the shows already in A (positions 4 and 12)
 */
function applyTierMoveOrderFix(a: Anime): Anime | null {
  if (a.grade !== 'A' || (a.order ?? 0) > 2) return null;
  if (a.id === 'ANIME_1792437441001_6px4') return { ...a, order: 13 }; // Shuiro no Kamen
  if (a.id === 'ANIME_1792437995180_6zco') return { ...a, order: 14 }; // Cyberpunk Edgerunners
  return null;
}

/**
 * JoJo Steel Ball Run: episode 1 came out alone on 19 Mar 2026, the weekly run started 25 Sep and
 * ends 4 Dec — it's a Fall 2026 show
 */
function applySteelBallRunSeasonFix(a: Anime): Anime | null {
  if (a.id === 'ANIME_1792644588506_8h6i' && a.season === 'WINTER' && a.year === 2026) {
    return { ...a, season: 'FALL' };
  }
  return null;
}

/**
 * The heavy-armor knight show is on AnimeRuka too, but its Thai-only title never matched there,
 * so it was linked to Bilibili + Waku only. Link it so its schedule follows AnimeRuka like the rest.
 */
const KNIGHT_RUKA_URL = 'https://animeruka.com/anime/tsuihou-sareta-tensei-juukishi-wa-game-chishiki-de-musou-suru/';
function applyKnightRukaLinkFix(a: Anime): Anime | null {
  if (a.id !== 'ANIME_1792641374853_lfav' || (a.sources || []).some((s) => s.url?.includes('animeruka.com'))) {
    return null;
  }
  return {
    ...a,
    sources: [
      ...(a.sources || []),
      { type: 'animeruka', name: 'AnimeRuka', url: KNIGHT_RUKA_URL, latestEp: a.latestEp, audio: 'ซับไทย' },
    ],
    scheduleSource: 'animeruka',
    detectedDayNote: 'ตามตาราง AnimeRuka: วันพฤหัสบดี 22:56 น.',
  };
}

/** ตำรวจคู่หูพลังจิต isn't on AnimeRuka: keep it on Ani-One's Tuesday 21:30 slot */
function applyPsychicCopsAniOneFix(a: Anime): Anime | null {
  if (a.id !== 'ANIME_1792672559646_jc1a' || a.scheduleSource === 'anione') return null;
  return {
    ...a,
    broadcastDay: 'TUE',
    broadcastTime: '21:30',
    scheduleSource: 'anione',
    detectedDayNote: 'ตามตาราง Ani-One: วันอังคาร 21:30 น.',
  };
}

/**
 * Bleach Calamity has 10 episodes (series eps 41-50; 49-50 were delayed to October). Its finale
 * turned up on 27 Oct, but the end date was set to the Summer season's end, so it skipped
 * "เพิ่งจบ" and dropped straight off the airing list. The saved 13 was a guess.
 */
function applyCalamityEndFix(a: Anime): Anime | null {
  if (a.id !== 'ANIME_1792441383077_ugkp' || a.finishedAt !== '2026-09-30') return null;
  return { ...a, totalEp: 10, finishedAt: '2026-10-27' };
}

/**
 * The Bleach TYBW parts re-added from the explore page (2026-10-26) picked up franchise-wide
 * episode numbers: Part 1 got Ani-One's all-parts playlist (49→50), Parts 2 and 3 took
 * AnimeRuka's / Bilibili's series numbering (ep 26, ep 40). Each part's own count: 13, 13, 14.
 */
const BLEACH_PART_COUNTS: Record<string, { inflated: number; eps: number }> = {
  ANIME_1792969447414_w7x2: { inflated: 49, eps: 13 }, // Sennen Kessen-hen
  ANIME_1792969445856_o1w0: { inflated: 26, eps: 13 }, // Ketsubetsu-tan
  ANIME_1792441380655_mve1: { inflated: 40, eps: 14 }, // Soukoku-tan
};
const BLEACH_ALL_PARTS_PLAYLIST = 'PL_YV8EWtbQXHorKU59EjVs-wWv47Jhq8d';
function applyBleachPartCountFix(a: Anime): Anime | null {
  const fix = BLEACH_PART_COUNTS[a.id];
  if (!fix || (Number(a.latestEp) || 0) < fix.inflated) return null;
  const sources = (a.sources || [])
    .filter((s) => !s.url.includes(BLEACH_ALL_PARTS_PLAYLIST))
    .map((s) => ({ ...s, latestEp: fix.eps }));
  return {
    ...a,
    sources,
    latestEp: fix.eps,
    totalEp: fix.eps,
    watchedEp: Math.min(Number(a.watchedEp) || 0, fix.eps),
    lastNotifiedEp: Math.min(Number(a.lastNotifiedEp) || fix.eps, fix.eps),
    episodes: [], // may hold the playlist's / series-numbered episodes; the detail popup re-fetches
  };
}

/** Ani-One's Part 3 playlist ("… -3", eps 27–40) got linked to Part 1 before "-N" read as a part */
const BLEACH_PART3_PLAYLIST = 'PL_YV8EWtbQXGSV8n6OJqp1eHGHZ7WnTb9';
function applyBleachPart3PlaylistFix(a: Anime): Anime | null {
  if (a.id !== 'ANIME_1792969447414_w7x2' || !(a.sources || []).some((s) => s.url.includes(BLEACH_PART3_PLAYLIST))) {
    return null;
  }
  return {
    ...a,
    sources: a.sources.filter((s) => !s.url.includes(BLEACH_PART3_PLAYLIST)),
    latestEp: 13,
    episodes: [],
  };
}

/**
 * One-time data fixes for entries that were linked to the wrong show/season/part.
 * Each runs once per browser (its own flag), and also on cloud lists pulled in that same session.
 */
const DATA_FIXES: Array<{ key: string; apply: (a: Anime) => Anime | null }> = [
  { key: 'anime_ranker_goblin_fix_v2', apply: applyGoblinFix },
  { key: 'anime_ranker_season_split_fix_v1', apply: applySeasonSplitFix },
  { key: 'anime_ranker_sub_dub_fix_v1', apply: applySubDubFix },
  { key: 'anime_ranker_bleach_part_fix_v1', apply: applyBleachPartFix },
  { key: 'anime_ranker_bleach_split_fix_v1', apply: applyBleachSplitFix },
  { key: 'anime_ranker_overgeared_season_fix_v1', apply: applyOvergearedSeasonFix },
  { key: 'anime_ranker_bilibili_season_fix_v1', apply: applyBilibiliSeasonFix },
  { key: 'anime_ranker_tier_move_order_fix_v1', apply: applyTierMoveOrderFix },
  { key: 'anime_ranker_steel_ball_run_season_fix_v1', apply: applySteelBallRunSeasonFix },
  { key: 'anime_ranker_knight_ruka_link_fix_v1', apply: applyKnightRukaLinkFix },
  { key: 'anime_ranker_psychic_cops_anione_fix_v1', apply: applyPsychicCopsAniOneFix },
  { key: 'anime_ranker_calamity_end_fix_v1', apply: applyCalamityEndFix },
  { key: 'anime_ranker_bleach_part_count_fix_v1', apply: applyBleachPartCountFix },
  { key: 'anime_ranker_bleach_part3_playlist_fix_v1', apply: applyBleachPart3PlaylistFix },
];

const pendingDataFixes = DATA_FIXES.filter((f) => {
  try {
    return localStorage.getItem(f.key) !== 'done';
  } catch {
    return false;
  }
});

function markDataFixesDone() {
  try {
    for (const f of DATA_FIXES) localStorage.setItem(f.key, 'done');
  } catch {}
}

export function repairKnownBadLinks(list: Anime[]): { list: Anime[]; changed: boolean } {
  // Always-on rules: per-season counts only (no franchise-wide compilation links), and no
  // status/schedule text saved as a Thai title
  const titled = clearGenericThaiTitles(list);
  const dropped = dropCompilationSources(titled.list);
  const compiled = { list: dropped.list, changed: titled.changed || dropped.changed };
  if (pendingDataFixes.length === 0) return compiled;
  let changed = compiled.changed;
  const next = compiled.list.map((a) => {
    let current = a;
    for (const fix of pendingDataFixes) current = fix.apply(current) || current;
    if (current !== a) changed = true;
    return current;
  });
  return { list: next, changed };
}

export function getLocalAnimeList(): Anime[] {
  try {
    const raw = localStorage.getItem(ANIME_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const baseList = Array.isArray(parsed) && parsed.length > 0 ? parsed : INITIAL_ANIME_LIST;

    const resetKey = 'anime_ranker_v5_black_clover_s2_episodes';
    if (localStorage.getItem(resetKey) !== 'done') {
      const { mergedList } = mergeDuplicateAnime(baseList);
      const cleanList = mergedList.map((a) => {
        let totalEp = a.totalEp;
        if (
          a.title.toLowerCase().includes('black clover') ||
          (a.thaiTitle && a.thaiTitle.includes('แบล็คโคลเวอร์'))
        ) {
          totalEp = 26; // Current season only (exclude season 1's 170 episodes)
        }
        return {
          ...a,
          totalEp: totalEp || a.totalEp,
          sources: (a.sources || []).filter(
            (s) =>
              s.type !== 'netflix' &&
              !(s.name && s.name.toLowerCase().includes('netflix')) &&
              !(s.url && s.url.toLowerCase().includes('netflix'))
          ),
        };
      });
      localStorage.setItem(resetKey, 'done');
      const repaired = repairKnownBadLinks(cleanList).list;
      markDataFixesDone();
      localStorage.setItem(ANIME_STORAGE_KEY, JSON.stringify(repaired));
      return repaired;
    }

    const repaired = repairKnownBadLinks(baseList);
    markDataFixesDone();
    if (repaired.changed) {
      saveLocalAnimeList(repaired.list);
      return repaired.list;
    }
    return baseList;
  } catch (e) {
    console.warn('Failed to load anime list from localStorage:', e);
  }
  return INITIAL_ANIME_LIST.map((a) => ({ ...a, watchedEp: 0 }));
}

/*
 * Cloud sync. Every device keeps its own copy in localStorage; the cloud copy (KV) carries the
 * time it was saved (updatedAt). Each device remembers which cloud version its copy is based on
 * (SYNCED_AT_KEY), so:
 * - on open / when the app comes back to the front, a newer cloud copy replaces the local one;
 * - nothing is pushed until the cloud copy has been checked, so a device with an old copy can't
 *   overwrite newer data before it has caught up;
 * - each push names the version it builds on, and the server refuses it (409) if another device
 *   saved since; the device then takes the cloud copy instead.
 */
const SYNCED_AT_KEY = 'anime_ranker_synced_at_v1';

function getSyncedAt(): number {
  try {
    return Number(localStorage.getItem(SYNCED_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function setSyncedAt(at: number) {
  try {
    localStorage.setItem(SYNCED_AT_KEY, String(at));
  } catch {}
}

let cloudChecked = false;
let queuedPush: Anime[] | null = null;
let pushing = false;
let onCloudList: ((list: Anime[]) => void) | null = null;

/** null = couldn't reach the cloud (an empty cloud comes back as an empty list) */
async function fetchRemoteKVAnimeList(): Promise<{ list: Anime[]; updatedAt: number } | null> {
  try {
    const res = await fetch('/api/anime', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    const list = Array.isArray(data?.animeList) ? data.animeList : [];
    return { list, updatedAt: Number(data?.updatedAt) || 0 };
  } catch {
    return null;
  }
}

/** Episodes out + episodes watched: an older copy of the same list never has more */
const progressOf = (list: Anime[]) =>
  list.reduce((n, a) => n + (Number(a.latestEp) || 0) + (Number(a.watchedEp) || 0), 0);

function readLocalList(): Anime[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(ANIME_STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Take the cloud copy if it's newer than the one this device is based on */
async function pullCloudList(): Promise<void> {
  const remote = await fetchRemoteKVAnimeList();
  if (!remote) return; // offline: keep local, push nothing yet
  const syncedAt = getSyncedAt();
  if (remote.list.length > 0 && remote.updatedAt > syncedAt) {
    const local = readLocalList();
    if (syncedAt === 0 && progressOf(local) > progressOf(remote.list)) {
      // First sync on this device and its copy is ahead of the cloud's: keep it and send it up
      setSyncedAt(remote.updatedAt);
      queuedPush = local;
    } else {
      // Local changes made on the older copy are dropped (and any push waiting with them)
      queuedPush = null;
      saveLocalAnimeList(remote.list, false);
      setSyncedAt(remote.updatedAt);
      onCloudList?.(remote.list);
    }
  }
  cloudChecked = true;
  void flushPush();
}

/** One push at a time (each builds on the version the last one saved); only the latest list goes */
async function flushPush(): Promise<void> {
  if (pushing || !queuedPush) return;
  pushing = true;
  const list = queuedPush;
  queuedPush = null;
  let status = 0;
  try {
    const res = await fetch('/api/anime', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ animeList: list, baseUpdatedAt: getSyncedAt() }),
    });
    status = res.status;
    if (res.ok) {
      // The server stamps the version (device clocks can disagree)
      const savedAt = Number((await res.json().catch(() => null))?.updatedAt);
      if (savedAt > 0) setSyncedAt(savedAt);
    }
  } catch {}
  pushing = false;
  if (status === 409) return pullCloudList();
  if (status >= 200 && status < 300) return flushPush();
  // Offline / server error: keep it for the next save or the next time the app comes to the front
  queuedPush = queuedPush || list;
}

/**
 * Start syncing with the cloud copy: checks it now and whenever the app comes back to the front
 * (phones keep the page open in the background). onList gets the cloud list when it replaces
 * the local one.
 */
export function startCloudSync(onList: (list: Anime[]) => void): () => void {
  onCloudList = onList;
  void pullCloudList();
  const onVisible = () => {
    if (document.visibilityState === 'visible') void pullCloudList();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    onCloudList = null;
    document.removeEventListener('visibilitychange', onVisible);
  };
}

export function saveLocalAnimeList(list: Anime[], syncToBackend = true): void {
  try {
    localStorage.setItem(ANIME_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('Failed to save anime list to localStorage:', e);
  }

  if (syncToBackend) {
    queuedPush = list;
    if (cloudChecked) void flushPush();
  }
}

export function getLineConfig(): LineNotifyConfig {
  try {
    const raw = localStorage.getItem(LINE_CONFIG_KEY);
    if (!raw) return DEFAULT_LINE_CONFIG;
    return { ...DEFAULT_LINE_CONFIG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_LINE_CONFIG;
  }
}

export function saveLineConfig(config: LineNotifyConfig): void {
  try {
    localStorage.setItem(LINE_CONFIG_KEY, JSON.stringify(config));
  } catch {}
}

export function exportAnimeBackupJSON(list: Anime[]): void {
  const payload = {
    version: '1.0.0',
    exportedAt: new Date().toISOString(),
    count: list.length,
    animeList: list,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `anime-tier-tracker-backup-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
